import { defineStore } from 'pinia';
import { getData, postData, putData, postFileData } from '@/services/axios-service';
import { BASE_URL } from '@/utils/constants/api-constants';
import {
  BookingForm,
  BookingState,
  AdditionalCost,
  ProfileDataForm,
  IContractData,
  IContractSignData,
  IContract,
  IBookingResponse,
  Tax,
} from '@/interfaces/booking/booking.interface';
import { IDocumentSignedManualForm } from '@/interfaces/profile/profile-response.interface';
import { useAuth } from '@/composables/useAuth';
import { PlaceLocation } from '@/interfaces/elastic/elastic.interface';

const INITIAL_STATE = {
  taxes: [],
  bookings: [],
  bookingForm: {
    RentalUnitId: '',
    startDate: '',
    endDate: '',
    additionalCostsIds: [],
    ContractModelId: '',
  },
  contractData: undefined,
  isLoading: false,
  total: 0,
  rentalUnitSelected: null,
  lastBooking: null,
  bookingSelected: null,
  additionalList: [],
  profileBillingDataForm: {
    email: '',
    name: '',
    documentId: '',
    documentType: '',
    Location: {
      address: null,
      fullAddress: null,
      lat: null,
      lon: null,
      country: null,
      countryCode: null,
      prefixPhone: null,
      state: null,
      city: null,
      street: null,
      postalCode: null,
    },
  },
  storedDataForm: {
    email: '',
    name: '',
    documentId: '',
    documentType: '',
    Location: {
      address: null,
      fullAddress: null,
      lat: null,
      lon: null,
      country: null,
      countryCode: null,
      prefixPhone: null,
      state: null,
      city: null,
      street: null,
      postalCode: null,
    },
  },
  toggleUploadManualContract: false,
};

export const useBookingStore = defineStore('BookingStore', {
  state: (): BookingState => ({
    ...INITIAL_STATE,
  }),
  actions: {
    async getAdditionalCosts(OwnerId: string) {
      try {
        const { data, success, error } = await getData<AdditionalCost[]>(
          `${BASE_URL.RENTALS}public/rates-extras`,
          {
            OwnerId,
          },
        );

        if (success && data) {
          this.additionalList = data;
          return data;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },
    async getLastBooking() {
      try {
        const { data, success, error } = await getData<BookingForm>(
          `${BASE_URL.RENTALS}tenant/reservations/last`,
        );

        if (success && data) {
          return data;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },
    async getBookings() {
      try {
        const { data, success, error } = await getData<IBookingResponse>(
          `${BASE_URL.RENTALS}tenant/reservations`,
        );

        if (success && data) {
          return data;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },
    async getTaxes(id: string) {
      try {
        const { data, success, error } = await getData<Tax[]>(
          `${BASE_URL.RENTALS}public/rental-units/elements/rates-taxes?InvoicingCentreId=${id}`,
        );

        if (success && data) {
          this.taxes = data.filter((tax) => tax.isDefault);
          return data;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },
    async createBooking() {
      try {
        const response = await postData<BookingForm>(`${BASE_URL.RENTALS}tenant/reservations`, {
          ...this.bookingForm,
        });

        if (response.success) {
          return response;
        } else {
          throw new Error(response.error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },

    async editTenantBillingInfo() {
      try {
        const response = await putData<ProfileDataForm>(`${BASE_URL.USERS}profile/billing-data`, {
          ...this.profileBillingDataForm,
        });

        if (response.success) {
          return response;
        } else {
          throw new Error(response.error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },
    async getTenantBillingData() {
      try {
        const { user } = useAuth();

        const res = await getData<ProfileDataForm>(`${BASE_URL.USERS}profile/billing-data`);
        const { success, data, error } = res;
        if (success && data) {
          const Location = user.value?.Location as PlaceLocation;
          this.profileBillingDataForm = { ...data, Location };
          this.storedDataForm = { ...data, Location };
          return res;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },
    async getBooking(bookingId: string) {
      try {
        const { data, success, error } = await getData<BookingForm>(
          `${BASE_URL.RENTALS}tenant/reservations/${bookingId}`,
        );

        if (success && data) {
          return data;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },
    async getReservationContract(bookingId: string) {
      try {
        const response = await getData<IContractData>(
          `${BASE_URL.DOCUMENT_SIGNING}tenant/contracts/${bookingId}`,
        );
        if (response.success) {
          return response;
        }
      } catch (error: unknown) {
        console.error(error);
      }
    },
    async signReservationContract(reservationId: string) {
      try {
        const response = await postData<IContractSignData>(
          `${BASE_URL.DOCUMENT_SIGNING}tenant/contracts/${reservationId}/signing`,
        );
        if (response.success) {
          return response;
        } else {
          throw new Error(response.error);
        }
      } catch (error: unknown) {
        const errorMsg = error?.toString() ?? 'Unknown error!';
        throw new Error(errorMsg);
      }
    },
    async signDownloadContract(reservationId: string) {
      try {
        return await postFileData<string>(
          `${BASE_URL.DOCUMENT_SIGNING}tenant/contracts/${reservationId}/download`,
        );
      } catch (error: unknown) {
        const errorMsg = error?.toString() ?? 'Unknown error!';
        throw new Error(errorMsg);
      }
    },
    async uploadSignedContract(reservationId: string, form: IDocumentSignedManualForm) {
      try {
        return await postFileData<IContract>(
          `${BASE_URL.DOCUMENT_SIGNING}tenant/contracts/${reservationId}/upload`,
          {
            ...form,
          },
        );
      } catch (error: unknown) {
        const errorMsg = error?.toString() ?? 'Unknown error!';
        throw new Error(errorMsg);
      }
    },
  },
});

export type BookingStore = ReturnType<typeof useBookingStore>;
